import { DEFAULT_DEBOUNCE_DELAY } from '@repo/ui';
import debounce from 'debounce';
import {
  CreateSectorParams,
  DeleteSectorParams,
  GetAllSectorsParams,
  GetSectorByIdParams,
  UpdateSectorParams,
} from 'domain/models';
import { CourseListUseCaseInterface } from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearSectors,
  clearSelectedSector,
  loadSectors,
  loadSelectedSector,
} from 'presentation/config/store/sectors/sectors-slice';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

type ModalMode = 'create' | 'edit';

type LoadingKey =
  | 'courses'
  | 'sectors'
  | 'sector'
  | 'createSector'
  | 'updateSector'
  | 'deleteSector';

export const useSectorsList = () => {
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [isModalFormOpen, setIsModalFormOpen] = useState(false);
  const [coursesSelectOptions, setCoursesSelectOptions] = useState<
    CourseListUseCaseInterface.Result[]
  >([]);
  const [paginationMetadata, setPaginationMetadata] = useState({
    selectedPage: 1,
    rowsPerPage: 10,
    searchString: '',
    status: 'ALL' as GetAllSectorsParams['status'],
  });
  const [loading, setLoading] = useState<Record<LoadingKey, boolean>>({
    courses: false,
    sectors: false,
    sector: false,
    createSector: false,
    updateSector: false,
    deleteSector: false,
  });

  const dispatch = useDispatch();
  const { sectorsService, courseService } = useService();
  const { sectors, selectedSector } = useAppSelector((state) => state.Sectors);

  const toggleModalForm = useCallback(() => {
    setIsModalFormOpen((prev) => !prev);
  }, []);

  const setLoadingState = (key: LoadingKey, value: boolean) => {
    setLoading((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Pagination
  const changePaginationMetadata = useCallback(
    <T extends keyof typeof paginationMetadata>(
      key: T,
      value: (typeof paginationMetadata)[T],
    ) => {
      setPaginationMetadata((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      changePaginationMetadata('selectedPage', page);
    },
    [changePaginationMetadata],
  );

  const handleSearchRemote = useCallback((searchString: string) => {
    changePaginationMetadata('searchString', searchString);
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    changePaginationMetadata('rowsPerPage', rowsPerPage);
  }, []);

  const handleStatusChange = (status: GetAllSectorsParams['status']) => {
    changePaginationMetadata('status', status);
    changePaginationMetadata('selectedPage', 1);
  };

  // Requests
  const requestCoursesForSelectOptions = useCallback(async () => {
    setLoadingState('courses', true);

    try {
      const res = await courseService.courseList();
      setCoursesSelectOptions(res);
    } catch (error) {
      notifyError(error?.message || 'Erro ao obter cursos');
    } finally {
      setLoadingState('courses', false);
    }
  }, [courseService]);

  const requestGetSectors = useCallback(async (params: GetAllSectorsParams) => {
    setLoadingState('sectors', true);

    try {
      const res = await sectorsService.getAllSectors(params);
      dispatch(loadSectors(res));
    } catch (error) {
      notifyError(error?.message || 'Erro ao obter setores');
      dispatch(clearSectors());
    } finally {
      setLoadingState('sectors', false);
    }
  }, []);

  const refreshSectors = useCallback(() => {
    requestGetSectors({
      page: paginationMetadata.selectedPage,
      length: paginationMetadata.rowsPerPage,
      status: paginationMetadata.status,
      query: paginationMetadata.searchString,
    });
  }, [requestGetSectors, paginationMetadata]);

  const requestGetSectorById = useCallback(
    async (params: GetSectorByIdParams) => {
      setLoadingState('sector', true);

      try {
        const res = await sectorsService.getSectorById(params);
        dispatch(loadSelectedSector(res));
      } catch (error) {
        notifyError(error?.message || 'Erro ao obter setor');
      } finally {
        setLoadingState('sector', false);
      }
    },
    [sectorsService, dispatch],
  );

  const requestCreateSector = useCallback(
    async (params: CreateSectorParams, onSuccess: () => void) => {
      setLoadingState('createSector', true);

      try {
        const res = await sectorsService.createSector(params);
        notifySuccess(res.message || 'Setor criado com sucesso!');
        refreshSectors();
        onSuccess();
      } catch (error) {
        notifyError(error?.message || 'Erro ao criar setor');
      } finally {
        setLoadingState('createSector', false);
      }
    },
    [sectorsService, refreshSectors],
  );

  const requestUpdateSector = useCallback(
    async (params: UpdateSectorParams, onSuccess: () => void) => {
      setLoadingState('updateSector', true);

      try {
        const res = await sectorsService.updateSector(params);
        notifySuccess(res.message || 'Setor atualizado com sucesso!');
        refreshSectors();
        onSuccess();
      } catch (error) {
        notifyError(error?.message || 'Erro ao atualizar setor');
      } finally {
        setLoadingState('updateSector', false);
      }
    },
    [sectorsService, refreshSectors],
  );

  const requestDeleteSector = useCallback(
    async (params: DeleteSectorParams) => {
      setLoadingState('deleteSector', true);

      try {
        const res = await sectorsService.deleteSector(params);
        notifySuccess(res.message || 'Setor excluído com sucesso!');
        refreshSectors();
      } catch (error) {
        notifyError(error?.message || 'Erro ao excluir setor');
      } finally {
        setLoadingState('deleteSector', false);
      }
    },
    [sectorsService, refreshSectors],
  );

  // Validation
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      sectorName: selectedSector?.name ?? null,
      description: selectedSector?.description ?? null,
      courses: selectedSector?.courses?.length
        ? selectedSector.courses.map((course) => ({
            value: course.id,
            label: course.name,
          }))
        : [],
      isActive: selectedSector?.isActive ?? true,
    },
    validationSchema: Yup.object({
      sectorName: Yup.string().required('Nome é obrigatório'),
      description: Yup.string().optional().nullable(),
      courses: Yup.array()
        .min(1, 'Selecione pelo menos um curso')
        .required('Selecione pelo menos um curso'),
      isActive: Yup.boolean(),
    }),
    onSubmit: (values, { resetForm: resetFormikForm }) => {
      const courseIds = values.courses?.map((course) => course.value) ?? [];

      const valuesToSubmit = {
        name: values.sectorName || undefined,
        description: values.description || undefined,
        courseIds,
      };

      const clearAfterSuccess = () => {
        resetFormikForm();
        dispatch(clearSelectedSector());
        toggleModalForm();
      };

      if (modalMode === 'edit') {
        requestUpdateSector(
          {
            id: selectedSector?.id,
            isActive: values.isActive,
            ...valuesToSubmit,
          },
          clearAfterSuccess,
        );
        return;
      }

      requestCreateSector(valuesToSubmit, clearAfterSuccess);
    },
  });

  const resetForm = useCallback(() => {
    formik.resetForm();
    dispatch(clearSelectedSector());
  }, [formik.resetForm, dispatch]);

  const debouncedRequestGetSectors = useMemo(
    () =>
      debounce((params: GetAllSectorsParams) => {
        requestGetSectors(params);
      }, DEFAULT_DEBOUNCE_DELAY),
    [requestGetSectors],
  );

  return {
    formik,
    resetForm,
    toggleModalForm,
    isModalFormOpen,

    paginationMetadata,
    handlePageChange,
    handleSearchRemote,
    handleRowsPerPageChange,
    handleStatusChange,
    debouncedRequestGetSectors,

    modalMode: {
      state: modalMode,
      set: setModalMode,
    },

    sectors: {
      data: sectors,
      loading: loading.sectors,
    },
    courses: {
      request: requestCoursesForSelectOptions,
      options: coursesSelectOptions,
      loading: loading.courses,
    },
    sector: {
      request: requestGetSectorById,
      loading: loading.sector,
    },
    createSector: {
      request: requestCreateSector,
      loading: loading.createSector,
    },
    updateSector: {
      request: requestUpdateSector,
      loading: loading.updateSector,
    },
    deleteSector: {
      request: requestDeleteSector,
      loading: loading.deleteSector,
    },
  };
};
